import Box from '@/common/components/Box';
import { ImageCarousel } from '@/common/components/ImageCarousel';
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder';
import SmallBadge from '@/common/components/SmallBadge';
import { formatUnit } from '@/common/utils/formatters';
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters';
import { useProposalModification } from '@/features/maintenance/features/establishmentChoice/features/auctions/hooks/useProposalModification';
import { UpdateExecutionEntryEstablishmentAcceptValidationSchema } from '@/features/maintenance/features/workflows/features/executionEntry/schemas/updateExecutionEntryEstablishmentAccept.schema';
import { DiagnosticActivityData } from '@/features/maintenance/features/workflows/types/forwardings/directiveEntries/diagnosticEntry/diagnosticEntry.type';
import { ClockIcon, WrenchIcon } from 'lucide-react';
import { Path, useFormContext, useWatch } from 'react-hook-form';
import EditProposalModal from '../../EditProposalModal';
import ProposalAwareValuesRow from '../../ProposalAwareValuesRow';

type FormSchema = UpdateExecutionEntryEstablishmentAcceptValidationSchema
type ModificationItem = FormSchema['activities'][number]

interface ActivityRowProps {
  executionActivity: DiagnosticActivityData;
  fieldIndex: number;
}

const ActivityRow = ({
  executionActivity,
  fieldIndex,
}: ActivityRowProps) => {
  const { control, setValue, formState: { disabled } } = useFormContext<FormSchema>()
  const itemFormState = useWatch({ control, name: `activities.${fieldIndex}` as Path<FormSchema> }) as ModificationItem | undefined

  const modification = useProposalModification({
    modification: {
      new_unit_price: itemFormState?.new_unit_price ? unFormatCurrency(itemFormState.new_unit_price) : null,
      new_warranty_in_days: itemFormState?.new_warranty_in_days
        ? unFormatUnknownUnit(itemFormState.new_warranty_in_days)
        : null,
    },
    unitPrice: executionActivity.unit_price,
    warrantyInDays: executionActivity.warranty_in_days,
    quantity: executionActivity.quantity,
    totalValue: executionActivity.total_value,
    disabled,
    onSave: (newUnitPrice, newWarrantyInDays) => {
      setValue(`activities.${fieldIndex}.new_unit_price` as Path<FormSchema>, newUnitPrice as never)
      setValue(`activities.${fieldIndex}.new_warranty_in_days` as Path<FormSchema>, newWarrantyInDays as never)
    },
  })

  const quantityDisplay =
    executionActivity.unit_for_quantity === 'minutes' ? (
      <SmallBadge
        LeftIcon={ClockIcon}
        text={formatUnit(executionActivity.quantity, 'hours')}
      />
    ) : (
      <SmallBadge
        text={formatUnit(executionActivity.quantity, 'item')}
      />
    );

  return (
    <Box className="p-2 rounded-lg">
      <div className="flex w-full items-start gap-4">
        <ImageCarousel
          files={
            executionActivity.images?.map((image) => ({
              id: image.id,
              file: image?.url,
            })) || []
          }
          Placeholder={
            <ImageWithPlaceholder className="size-20 rounded-full" iconClassName="size-10" placeholderIcon={WrenchIcon} />
          }
          className="size-20"
          showExpandButton
          showRemoveButton={false}
        />

        <div className="flex flex-col gap-1 flex-1">
          <div className="flex items-center gap-3">
            <ProposalAwareValuesRow
              originalUnitPrice={modification.originalUnitPrice}
              displayNewUnitPrice={modification.displayNewUnitPrice}
              unitPricePercentage={modification.unitPricePercentage}
              unitPriceLabel={executionActivity.unit_for_quantity === 'minutes' ? 'Valor por hora' : 'Valor unitário'}
              originalTotalValue={modification.originalTotalValue}
              displayNewTotalValue={modification.displayNewTotalValue}
              totalValuePercentage={modification.totalValuePercentage}
              originalWarranty={modification.originalWarranty}
              displayNewWarranty={modification.displayNewWarranty}
              quantityDisplay={quantityDisplay}
            />

            <EditProposalModal
              title={`Editar ${executionActivity?.display_name || '--'}`}
              isOpen={modification.isOpen}
              setIsOpen={modification.handleOpenChange}
              onConfirm={modification.onConfirm}
              localUnitPrice={modification.localUnitPrice}
              setLocalUnitPrice={modification.setLocalUnitPrice}
              localWarranty={modification.localWarranty}
              setLocalWarranty={modification.setLocalWarranty}
              originalUnitPrice={modification.originalUnitPrice}
              originalTotalValue={modification.originalTotalValue}
              originalWarranty={modification.originalWarranty}
              previewTotalValue={modification.previewTotalValue}
              unitPriceLabel={executionActivity.unit_for_quantity === 'minutes' ? 'Valor por hora' : 'Valor unitário'}
              quantityDisplay={quantityDisplay}
              disabled={modification.disabled}
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col gap-2">
              <p className="font-bold text-base">
                {executionActivity?.display_name || '--'}
              </p>

              {executionActivity.technical_professionals?.length > 0 && (
                <p className="text-xs">
                  {executionActivity.technical_professionals?.map(({ display_name }) => display_name).join(', ')}
                </p>
              )}
            </div>
          </div>

          <p className="text-xs">
            {executionActivity.description}
          </p>
        </div>
      </div>
    </Box>
  )
}

export default ActivityRow
